/**
 * lib/budgets.ts
 * ------------------------------------------------------------------
 * WHAT THE SCHOOL PLANNED TO SPEND, AND WHAT IT HAS.
 *
 * A budget is only useful in the middle of a year. At the end it is
 * history, and at the start it is a wish; the question worth answering
 * in September is "at this point in the year, should we have spent this
 * much?" — which is not the same as whether the annual figure has been
 * breached, and is the question a simple total cannot answer.
 *
 * SO EVERY VARIANCE IS AGAINST THE BUDGET TO DATE, not the annual one.
 * A department that has spent 90% of its year's budget is fine in
 * November and in trouble in March, and a screen reporting only "90%
 * used" cannot tell you which.
 *
 * DIRECTION IS THE THING MOST OFTEN GOT WRONG. Overspending an expense
 * is bad; over-collecting income is good. The same arithmetic — actual
 * minus budget — means opposite things depending on the account, and a
 * report that colours both red teaches people to ignore the colour.
 * `favourable` carries that judgement so no screen has to re-derive it.
 *
 * BUDGETS ARE PER ACCOUNT, NOT PER COST CENTRE. LKC has not defined
 * cost centres, and inventing an axis the school does not use would
 * produce a report nobody can act on. Accounts are what the chart
 * already has and what the ledger already posts to.
 *
 * PURE. Budgets and journals in, variances out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { GlAccount, GlJournal, AccountType } from '@/lib/gl';

export const BUDGET_COLLECTION = 'financeBudgets';

/**
 * One account's budget for one year.
 *
 * `monthly` holds twelve figures when the school has phased it, and is
 * absent when it has not. Absent means spread evenly — stated here so
 * that "no phasing" and "phased flat" are the same thing rather than two
 * states that can drift.
 */
export interface AccountBudget {
  /** `${fy}_${accountCode}` — one row per account per year. */
  id: string;
  fy: string;
  accountCode: string;
  annual: number;
  /** Twelve figures, in the financial year's own month order. */
  monthly?: number[];
  note?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** The budget for a month index (0-11), phased or spread evenly. */
export function budgetForMonth(b: AccountBudget, monthIdx: number): number {
  if (b.monthly && b.monthly.length === 12) return round2(Number(b.monthly[monthIdx]) || 0);
  return round2((Number(b.annual) || 0) / 12);
}

/** The budget from the start of the year up to and including a month. */
export function budgetToDate(b: AccountBudget, monthIdx: number): number {
  let n = 0;
  for (let i = 0; i <= monthIdx; i += 1) n += budgetForMonth(b, i);
  return round2(n);
}

/**
 * A phasing that does not add up to the annual figure is a real error —
 * the annual figure is what was approved, and twelve months that total
 * something else means one of the two is wrong.
 */
export function phasingProblem(b: AccountBudget): string | null {
  if (!b.monthly) return null;
  if (b.monthly.length !== 12) return 'A phased budget needs twelve figures.';
  const sum = round2(b.monthly.reduce((n, m) => n + (Number(m) || 0), 0));
  const annual = round2(Number(b.annual) || 0);
  if (Math.abs(sum - annual) > 0.005) {
    return `The twelve months add to ${sum.toFixed(2)} but the annual budget is `
      + `${annual.toFixed(2)}. One of the two is wrong.`;
  }
  return null;
}

/** An even spread, for a school starting from a single annual figure. */
export function evenPhasing(annual: number): number[] {
  const each = Math.floor((round2(annual) * 100) / 12) / 100;
  const out = Array.from({ length: 12 }, () => each);
  /* The remainder lands in the last month rather than being lost. Twelve
     even thirds of a pula do not exist, and a phasing that quietly totals
     a thebe less than the annual figure fails its own check. */
  out[11] = round2(annual - each * 11);
  return out;
}

export interface BudgetLine {
  accountCode: string;
  accountName: string;
  type: AccountType;
  annual: number;
  budgetToDate: number;
  actualToDate: number;
  /** actual − budget. Sign alone does not say whether that is good news. */
  variance: number;
  variancePct: number;
  /**
   * Whether the variance is GOOD. Under on an expense and over on income
   * are both favourable; the same number means opposite things.
   */
  favourable: boolean;
  /** Proportion of the annual budget used, whatever the month. */
  usedPct: number;
  note?: string;
}

export interface BudgetReport {
  fy: string;
  /** 0-11, the last month included. */
  throughMonth: number;
  lines: BudgetLine[];
  income: { budget: number; actual: number; variance: number };
  expense: { budget: number; actual: number; variance: number };
  /** Lines where spending has run ahead of the year, worst first. */
  overspent: BudgetLine[];
  /** Accounts with real movement and no budget at all. */
  unbudgeted: { accountCode: string; accountName: string; actual: number }[];
}

/**
 * Compare the ledger against the budget, up to a month.
 *
 * `monthOrder` is the financial year's own month sequence — an April
 * school's first month is April, and comparing its April spend against
 * a January budget would be wrong in a way nobody would spot.
 */
export function buildBudgetReport(
  fy: string,
  throughMonth: number,
  budgets: AccountBudget[],
  accounts: GlAccount[],
  journals: GlJournal[],
  monthOrder: string[],
): BudgetReport {
  const upTo = new Set(monthOrder.slice(0, throughMonth + 1));

  /* Actuals on the account's own normal side: expenses as debits, income
     as credits. Reporting income as a negative because it credits would
     make every income variance read backwards. */
  const actual = new Map<string, number>();
  for (const j of journals) {
    const month = (j.date ?? '').slice(0, 7);
    if (!upTo.has(month)) continue;
    for (const l of j.lines ?? []) {
      const acct = accounts.find((a) => a.code === l.accountCode);
      if (!acct) continue;
      const signed = acct.type === 'income'
        ? (Number(l.cr) || 0) - (Number(l.dr) || 0)
        : (Number(l.dr) || 0) - (Number(l.cr) || 0);
      actual.set(l.accountCode, round2((actual.get(l.accountCode) ?? 0) + signed));
    }
  }

  const byCode = new Map(budgets.filter((b) => b.fy === fy).map((b) => [b.accountCode, b]));
  const lines: BudgetLine[] = [];

  for (const [code, b] of byCode) {
    const acct = accounts.find((a) => a.code === code);
    if (!acct) continue;
    const btd = budgetToDate(b, throughMonth);
    const atd = actual.get(code) ?? 0;
    const variance = round2(atd - btd);

    lines.push({
      accountCode: code,
      accountName: acct.name,
      type: acct.type,
      annual: round2(b.annual),
      budgetToDate: btd,
      actualToDate: atd,
      variance,
      variancePct: btd !== 0 ? round2((variance / Math.abs(btd)) * 100) : 0,
      favourable: acct.type === 'income' ? variance >= 0 : variance <= 0,
      usedPct: b.annual !== 0 ? round2((atd / b.annual) * 100) : 0,
      note: b.note,
    });
  }

  lines.sort((a, b) => a.accountCode.localeCompare(b.accountCode));

  const sumOf = (t: AccountType, f: 'budgetToDate' | 'actualToDate') =>
    round2(lines.filter((l) => l.type === t).reduce((n, l) => n + l[f], 0));

  const incomeB = sumOf('income', 'budgetToDate');
  const incomeA = sumOf('income', 'actualToDate');
  const expenseB = sumOf('expense', 'budgetToDate');
  const expenseA = sumOf('expense', 'actualToDate');

  /* Money spent against an account nobody budgeted for. Not an error —
     a school legitimately spends on things it did not foresee — but it
     is the difference between a budget that was wrong and a budget that
     was ignored, and only one of those is worth a conversation. */
  const unbudgeted = [...actual.entries()]
    .filter(([code]) => !byCode.has(code))
    .map(([code, amount]) => {
      const a = accounts.find((x) => x.code === code);
      return { accountCode: code, accountName: a?.name ?? code, actual: amount, type: a?.type };
    })
    .filter((u) => u.type === 'expense' && Math.abs(u.actual) > 0.005)
    .map(({ accountCode, accountName, actual: amt }) => ({ accountCode, accountName, actual: amt }))
    .sort((a, b) => b.actual - a.actual);

  return {
    fy,
    throughMonth,
    lines,
    income: { budget: incomeB, actual: incomeA, variance: round2(incomeA - incomeB) },
    expense: { budget: expenseB, actual: expenseA, variance: round2(expenseA - expenseB) },
    overspent: lines.filter((l) => !l.favourable).sort((a, b) => {
      const av = Math.abs(a.variance);
      const bv = Math.abs(b.variance);
      return bv - av;
    }),
    unbudgeted,
  };
}
